import CryptoJS from 'crypto-js'

const STORAGE_KEY = 'mypeegu_vc_school_auth'

export const SCHOOL_SESSION_MS = 90 * 60 * 1000
export const SCHOOL_SESSION_WARNING_MS = 5 * 60 * 1000

const getSecret = () => process.env.REACT_APP_USERLOCALSTORAGE || 'mypeegu_vc_school_secret'

export function getVcSchoolSession() {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		if (!stored) return null
		const decrypted = CryptoJS.AES.decrypt(stored, getSecret()).toString(CryptoJS.enc.Utf8)
		if (!decrypted) return null
		return JSON.parse(decrypted)
	} catch {
		return null
	}
}

export function setVcSchoolSession(session) {
	const encrypted = CryptoJS.AES.encrypt(JSON.stringify(session), getSecret()).toString()
	localStorage.setItem(STORAGE_KEY, encrypted)
}

export function clearVcSchoolSession() {
	localStorage.removeItem(STORAGE_KEY)
}

export function isVcSchoolLoggedIn() {
	const session = getVcSchoolSession()
	return Boolean(session?.token) && !isSchoolSessionExpired(session)
}

export function buildSchoolSessionPayload(token, account, serverMeta = {}) {
	const loginTime = serverMeta.loginTime
		? new Date(serverMeta.loginTime).getTime()
		: Date.now()
	const tokenExpiryTime = serverMeta.tokenExpiryTime
		? new Date(serverMeta.tokenExpiryTime).getTime()
		: loginTime + SCHOOL_SESSION_MS

	return {
		token,
		userId: account.userId || account._id || account.username,
		role: account.role || 'school',
		loginTime,
		tokenExpiryTime,
		username: account.username,
		email: account.email || '',
		schoolId: account.schoolId,
		schoolName: account.schoolName,
		displayName: account.displayName || '',
	}
}

export function isSchoolSessionExpired(session) {
	if (!session?.token) return true
	if (!session.loginTime && !session.tokenExpiryTime) return true
	if (session.tokenExpiryTime && Date.now() >= session.tokenExpiryTime) return true
	if (session.loginTime && Date.now() - session.loginTime >= SCHOOL_SESSION_MS) return true
	return false
}

export function getSchoolSessionRemainingMs(session) {
	if (!session?.tokenExpiryTime) {
		if (!session?.loginTime) return 0
		return Math.max(0, session.loginTime + SCHOOL_SESSION_MS - Date.now())
	}
	return Math.max(0, session.tokenExpiryTime - Date.now())
}

export const ACTIVE_MEETING_KEY = 'mypeegu_active_meeting'
export const COUNSELLOR_ACTIVE_MEETING_KEY = 'mypeegu_counsellor_active_meeting'

export function getActiveMeetingForRejoin() {
	try {
		const raw = localStorage.getItem(ACTIVE_MEETING_KEY)
		return raw ? JSON.parse(raw) : null
	} catch {
		return null
	}
}

export function setActiveMeetingForRejoin(meeting) {
	if (!meeting?.meetingId) return
	localStorage.setItem(
		ACTIVE_MEETING_KEY,
		JSON.stringify({
			meetingId: meeting.meetingId,
			sessionId: meeting.sessionId || '',
			schoolName: meeting.schoolName || '',
			schoolId: meeting.schoolId || '',
			title: meeting.title || '',
			expiresAt: meeting.expiresAt || null,
			leftAt: new Date().toISOString(),
		}),
	)
}

export function clearActiveMeetingForRejoin() {
	localStorage.removeItem(ACTIVE_MEETING_KEY)
}

export function getCounsellorMeetingForRejoin() {
	try {
		const raw = localStorage.getItem(COUNSELLOR_ACTIVE_MEETING_KEY)
		return raw ? JSON.parse(raw) : null
	} catch {
		return null
	}
}

export function setCounsellorMeetingForRejoin(meeting) {
	if (!meeting?.meetingId) return
	localStorage.setItem(
		COUNSELLOR_ACTIVE_MEETING_KEY,
		JSON.stringify({
			meetingId: meeting.meetingId,
			sessionId: meeting.sessionId || '',
			schoolName: meeting.schoolName || '',
			schoolId: meeting.schoolId || '',
			title: meeting.title || '',
			leftAt: new Date().toISOString(),
		}),
	)
}

export function clearCounsellorMeetingForRejoin() {
	localStorage.removeItem(COUNSELLOR_ACTIVE_MEETING_KEY)
}

/** Minutes left in the live meeting window (typically 60 min session) */
export function getMeetingSessionRemainingMs(meeting) {
	if (!meeting) return 0
	const now = Date.now()
	if (meeting.expiresAt) {
		return Math.max(0, new Date(meeting.expiresAt).getTime() - now)
	}
	const start = meeting.startTime || meeting.scheduledTime
	if (start) {
		const endMs = new Date(start).getTime() + 60 * 60 * 1000
		return Math.max(0, endMs - now)
	}
	const saved = getActiveMeetingForRejoin()
	if (saved?.leftAt) {
		const endMs = new Date(saved.leftAt).getTime() + 60 * 60 * 1000
		return Math.max(0, endMs - now)
	}
	return 0
}

export function formatMeetingRemainingLabel(remainingMs) {
	if (!remainingMs || remainingMs <= 0) return 'Session ended'
	const totalSec = Math.ceil(remainingMs / 1000)
	const mins = Math.floor(totalSec / 60)
	const secs = totalSec % 60
	if (mins >= 1) return `${mins} min left in session`
	return `${secs} sec left in session`
}
